import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { StationType } from '../../../constants/Games/gameStation';
import { getStationVisual } from '../../../constants/Games/gameStationUtils';
import { getGameTypeDetails } from '../../../constants/Games/gameType';
import { Icons } from '../../../constants/icons';
import { SVGIconType } from '../../../constants/svgIcon';
import { usePopup } from '../../../context';
import { StationDraft } from '../../../models/DraftStore';
import {
  GameTypeHelpPopup,
  GameTypeHelpType,
  stringToGameTypeHelpType,
} from '../../Popups';
import { FillPNGIcon } from '../FillPNGIcon';
import { SquareCheckbox, SquareCheckboxProps } from '../SquareCheckbox';
import { SvgIcon } from '../SVGIcon';

import styles from './CreateMemoryGameStationSelectCell.module.css';

type CreateMemoryGameStationSelectCellProps = {
  stationLabel?: number | string;
  gameType?: string;
} & Pick<StationDraft, 'stationItem' | 'stationType' | 'title' | 'subtitle'> &
  SquareCheckboxProps;

export const CreateMemoryGameStationSelectCell = (
  props: CreateMemoryGameStationSelectCellProps,
) => {
  const { gameType, title, subtitle, checked, disabled, onChange } = props;
  const { t } = useTranslation();
  const { stationLabel, stationItem, stationType } = props;
  const stationImageSource = getStationVisual(stationItem)?.filePrefix;

  const gameTypeIcon =
    stationType === StationType.Game || stationType === StationType.AssistedGame
      ? getGameTypeDetails(gameType ?? '').icon
      : undefined;
  const gameTypeSvgIcon =
    stationType === StationType.Game || stationType === StationType.AssistedGame
      ? getGameTypeDetails(gameType ?? '').svgIcon
      : undefined;
  const gameTypeName =
    stationType === StationType.Game || stationType === StationType.AssistedGame
      ? getGameTypeDetails(gameType ?? '').displayName
      : undefined;

  const { showPopup, hidePopup } = usePopup();

  const onClickInfo = useCallback(
    (type: GameTypeHelpType) => {
      showPopup(<GameTypeHelpPopup type={type} onPrimary={hidePopup} />);
    },
    [hidePopup, showPopup],
  );
  const popupInfoType = stringToGameTypeHelpType(
    stationType === StationType.AssistedGame ? 'assisted' : gameType,
  );

  return (
    <div className={styles.container}>
      <div className={styles.stationContainer}>
        <img
          src={stationImageSource}
          className={styles.stationImage}
          alt="Station Image"
        />
        {/* {stationLabel && <p className={styles.stationLabel}>{stationLabel}</p>} */}
      </div>
      {stationType === StationType.AssistedGame && (
        <div className={styles.cardContainer}>
          <div className={styles.cardHeader}>
            <div className={styles.titleContainer}>
              <div className={`${styles.typeTitle} ${styles.orangeColor}`}>
                {stationLabel ? (
                  <span className={styles.prefixCode}>{stationLabel}</span>
                ) : null}
                {t('Interactive Learning')}
              </div>
              <div className={styles.description}>
                <span className={styles.icon}>
                  {gameTypeSvgIcon ? (
                    <SvgIcon
                      name={gameTypeSvgIcon}
                      height={20}
                      width={20}
                      color={'#808080'}
                      style={{ minWidth: '24px' }}
                    />
                  ) : gameTypeIcon && Icons[gameTypeIcon] ? (
                    <div className={styles.gameTypeIcon}>
                      <FillPNGIcon
                        src={Icons[gameTypeIcon]}
                        color="#808080"
                        size={20}
                      />
                    </div>
                  ) : null}
                </span>
                {gameTypeName ? t(gameTypeName) : null}
              </div>
            </div>
            <SvgIcon
              name={SVGIconType.GameInstruction}
              className={styles.infoIcon}
              onClick={() => popupInfoType && onClickInfo(popupInfoType)}
            />
          </div>
          <hr />
          <div className={styles.cardContent}>
            <p className={styles.gameTitle}>{title}</p>
            <p className={styles.gameSubtitle}>{subtitle}</p>
          </div>
          <p />
        </div>
      )}
      {stationType === StationType.Game && (
        <div className={styles.cardContainer}>
          <div className={styles.cardHeader}>
            <div className={styles.titleContainer}>
              <div className={`${styles.typeTitle} ${styles.cyanColor}`}>
                {stationLabel ? (
                  <span className={styles.prefixCode}>{stationLabel}</span>
                ) : null}
                {t('Memory Practice')}
              </div>
              <div className={styles.description}>
                <span className={styles.icon}>
                  {gameTypeSvgIcon ? (
                    <SvgIcon
                      name={gameTypeSvgIcon}
                      height={20}
                      width={20}
                      color={'#808080'}
                      style={{ minWidth: '24px' }}
                    />
                  ) : gameTypeIcon && Icons[gameTypeIcon] ? (
                    <div className={styles.gameTypeIcon}>
                      <FillPNGIcon
                        src={Icons[gameTypeIcon]}
                        color="#808080"
                        size={20}
                      />
                    </div>
                  ) : null}
                </span>
                {gameTypeName ? t(gameTypeName) : null}
              </div>
            </div>
            <SvgIcon
              name={SVGIconType.GameInstruction}
              className={styles.infoIcon}
              onClick={() => popupInfoType && onClickInfo(popupInfoType)}
            />
          </div>
          <hr />
          <div className={styles.cardContent}>
            <p className={styles.gameTitle}>{title}</p>
            <p className={styles.gameSubtitle}>{subtitle}</p>
          </div>
          <p />
        </div>
      )}
      {stationType === StationType.PathChallenge && (
        <div className={styles.superChallengeContainer}>
          <SvgIcon
            name={SVGIconType.MiniTarget}
            width={24}
            height={24}
            color="black"
          />
          <p className={styles.superChallengeTitle}>{t('Mini Challenge')}</p>
        </div>
      )}
      {stationType === StationType.SuperChallenge && (
        <div className={styles.superChallengeContainer}>
          <SvgIcon
            name={SVGIconType.SuperTarget}
            width={24}
            height={24}
            color="black"
          />
          <p className={styles.superChallengeTitle}>{t('Super Challenge')}</p>
        </div>
      )}
      <SquareCheckbox
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
    </div>
  );
};
